import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SwipeCard } from './SwipeCard';
import { MatchModal } from './MatchModal';
import {
  discoveryService,
  type DiscoveryProfileCard,
  type ReceivedNudge,
  type SwipeResult,
} from '../../services/api/discoveryService';
import { safetyService, type ReportReason } from '../../services/api/safetyService';
import { useUser } from '../../context/UserContext';
import './DiscoveryPage.css';

const timeAgo = (iso: string): string => {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

interface DiscoveryPageProps {
  onOpenMessages?: () => void;
}

export const DiscoveryPage: React.FC<DiscoveryPageProps> = ({ onOpenMessages }) => {
  const { user } = useUser();
  const [profiles, setProfiles] = useState<DiscoveryProfileCard[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [matchedProfile, setMatchedProfile] = useState<DiscoveryProfileCard | null>(null);
  const [lastAction, setLastAction] = useState<{ profileId: string; action: string } | null>(null);
  const [likesCount, setLikesCount] = useState(0);
  const [unseenNudges, setUnseenNudges] = useState(0);
  const [showNudgePanel, setShowNudgePanel] = useState(false);
  const [nudges, setNudges] = useState<ReceivedNudge[]>([]);
  const [nudgesLoading, setNudgesLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimerRef = useRef<number | null>(null);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const feed = await discoveryService.getDiscoveryFeed(20);
      setProfiles(feed);
      setCurrentIndex(0);
    } catch (err) {
      setError('Failed to load profiles. Please try again.');
      console.error('Failed to load discovery feed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  // Header badge counts: likes received + unseen winks
  useEffect(() => {
    discoveryService.getLikesCount().then(setLikesCount);
    discoveryService.getUnseenNudgeCount().then(setUnseenNudges);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  const showToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(null), 2500);
  }, []);

  // Load more profiles when running low
  useEffect(() => {
    if (profiles.length - currentIndex <= 3 && !loading) {
      loadMoreProfiles();
    }
  }, [currentIndex, profiles.length, loading]);

  const loadMoreProfiles = async () => {
    try {
      const moreProfiles = await discoveryService.getDiscoveryFeed(10);
      // Filter out any duplicates
      const existingIds = new Set(profiles.map((p) => p.profileId));
      const newProfiles = moreProfiles.filter((p) => !existingIds.has(p.profileId));
      if (newProfiles.length > 0) {
        setProfiles([...profiles, ...newProfiles]);
      }
    } catch (err) {
      console.error('Failed to load more profiles:', err);
    }
  };

  const handleSwipe = async (action: 'like' | 'pass' | 'superlike') => {
    const profile = profiles[currentIndex];
    if (!profile) return;

    // Optimistically move to next card
    const previousIndex = currentIndex;
    setCurrentIndex(currentIndex + 1);
    setLastAction({ profileId: profile.profileId, action });

    try {
      const result: SwipeResult = await discoveryService.swipe(profile.profileId, action);

      if (result.isMatch && result.matchedProfile) {
        setMatchedProfile(result.matchedProfile);
      }
    } catch (err) {
      console.error('Swipe failed:', err);
      // Revert on error
      setCurrentIndex(previousIndex);
      setLastAction(null);
    }
  };

  const handleUndo = async () => {
    if (!lastAction) return;

    const success = await discoveryService.undoLastSwipe();
    if (success) {
      setCurrentIndex(Math.max(0, currentIndex - 1));
      setLastAction(null);
    }
  };

  const handleLike = () => handleSwipe('like');
  const handlePass = () => handleSwipe('pass');
  const handleSuperLike = () => handleSwipe('superlike');

  const handleWink = async () => {
    const profile = profiles[currentIndex];
    if (!profile) return;
    const result = await discoveryService.sendNudge(profile.profileId);
    showToast(result.success ? 'Wink sent 👋' : 'Winks are unavailable right now');
  };

  const handleOpenNudges = async () => {
    setShowNudgePanel(true);
    setNudgesLoading(true);
    const received = await discoveryService.getNudges();
    setNudges(received);
    setNudgesLoading(false);
    await discoveryService.markNudgesSeen();
    setUnseenNudges(0);
  };

  const handleCloseMatch = () => {
    setMatchedProfile(null);
  };

  const handleSendMessage = () => {
    setMatchedProfile(null);
    onOpenMessages?.();
  };

  // Safety handlers
  const handleBlock = async (profileId: string) => {
    const result = await safetyService.blockUser(profileId);
    if (result.success) {
      // Remove the blocked profile and move to next
      setProfiles(prev => prev.filter(p => p.profileId !== profileId));
    } else {
      console.error('Failed to block user:', result.error);
    }
  };

  const handleReport = async (profileId: string, reason: ReportReason, details?: string) => {
    const result = await safetyService.reportUser(profileId, reason, details);
    if (!result.success) {
      console.error('Failed to report user:', result.error);
    }
    // Optionally move to next profile after reporting
  };

  const currentProfile = profiles[currentIndex];

  if (loading && profiles.length === 0) {
    return (
      <div className="discovery-page">
        <div className="discovery-loading">
          <div className="spinner" />
          <p>Finding people near you...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="discovery-page">
        <div className="discovery-error">
          <h3>Oops!</h3>
          <p>{error}</p>
          <button onClick={loadProfiles}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="discovery-page">
      <div className="discovery-header">
        <h1>Discover</h1>
        <div className="discovery-badges">
          <span className="discovery-badge" title="Likes received">
            ❤ {likesCount}
          </span>
          <button
            className="discovery-badge discovery-badge-nudges"
            onClick={handleOpenNudges}
            title="Winks received"
          >
            👋 {unseenNudges}
          </button>
        </div>
        {lastAction && (
          <button className="undo-button" onClick={handleUndo} title="Undo last swipe">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z" />
            </svg>
          </button>
        )}
      </div>

      {!currentProfile ? (
        <div className="discovery-empty">
          <div className="empty-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
          <h3>You've seen everyone!</h3>
          <p>Check back later for new profiles</p>
          <button onClick={loadProfiles}>Refresh</button>
        </div>
      ) : (
        <>
          <div className="discovery-content">
            <SwipeCard
              key={currentProfile.profileId}
              profile={currentProfile}
              onLike={handleLike}
              onPass={handlePass}
              onSuperLike={handleSuperLike}
              onWink={handleWink}
              onBlock={handleBlock}
              onReport={handleReport}
            />
          </div>

          {/* Profile queue indicator */}
          <div className="queue-indicator">
            {profiles.length - currentIndex} profiles remaining
          </div>
        </>
      )}

      {/* Received winks panel */}
      {showNudgePanel && (
        <div className="nudge-panel-overlay" onClick={() => setShowNudgePanel(false)}>
          <div className="nudge-panel" onClick={(e) => e.stopPropagation()}>
            <div className="nudge-panel-header">
              <h3>👋 Winks</h3>
              <button
                className="nudge-panel-close"
                onClick={() => setShowNudgePanel(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            {nudgesLoading && <p className="nudge-panel-empty">Loading…</p>}
            {!nudgesLoading && nudges.length === 0 && (
              <p className="nudge-panel-empty">No winks yet — keep browsing!</p>
            )}
            {!nudgesLoading && nudges.length > 0 && (
              <ul className="nudge-list">
                {nudges.map((nudge) => (
                  <li key={nudge.id} className="nudge-item">
                    {nudge.fromProfile.primaryPhotoUrl ? (
                      <img
                        className="nudge-avatar"
                        src={nudge.fromProfile.primaryPhotoUrl}
                        alt={nudge.fromProfile.displayName}
                      />
                    ) : (
                      <div className="nudge-avatar nudge-avatar-placeholder">
                        {nudge.fromProfile.displayName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="nudge-item-info">
                      <span className="nudge-item-name">
                        {nudge.fromProfile.displayName}, {nudge.fromProfile.age}
                      </span>
                      <span className="nudge-item-meta">
                        winked at you · {timeAgo(nudge.createdAt)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Wink feedback toast */}
      {toast && <div className="discovery-toast">{toast}</div>}

      {/* Match modal */}
      {matchedProfile && (
        <MatchModal
          matchedProfile={matchedProfile}
          currentUserName={user?.displayName}
          currentUserPhotoUrl={user?.avatar}
          onClose={handleCloseMatch}
          onSendMessage={handleSendMessage}
          onKeepBrowsing={handleCloseMatch}
        />
      )}
    </div>
  );
};

export default DiscoveryPage;
